import {Component, ElementRef, computed, effect, input, output, signal, viewChild} from '@angular/core';
import {
  RoleGrantInvitation, bindingSentence, inviteDeliveryFailed, inviteDeliveryMessage, inviteLink, roleLabel,
  scopeDescription
} from './grant.models';

/**
 * The one moment an invitation code exists.
 *
 * ### Why this is a dialog with no accidental exit
 *
 * The backend stores an unsalted SHA-256 of the code and nothing else, so the response that created the
 * invitation is the **only** place the plaintext will ever appear — no later read can return it, and there is
 * no "show it again". An invitation whose code was closed away is not recoverable, only re-issuable.
 *
 * That single fact drives every decision here:
 *
 * - **The invite LINK is the headline, not the bare code.** What a recipient needs is
 *   `/invite?code=…` — the anonymous landing page that previews the invitation and starts the provider
 *   sign-in. A bare code is only useful to somebody who already knows where to paste it, so leading with it
 *   would hand over the less useful half.
 * - **Nothing dismisses this by accident.** No backdrop click, no `Escape`, no auto-close after a copy: the
 *   only exit is the explicit button, whose label states what closing costs. A modal that closes on a stray
 *   click is the exact shape that loses an unrecoverable secret.
 * - **The warning is a `caution` surface, not a footnote**, and it says what to do when it is too late
 *   (re-issue), because that is the only remedy there is.
 * - **The code is never persisted.** Not to `localStorage` (which `CookieService` traps anyway), not to a
 *   store, not to the invitation list — it lives in this component's inputs for as long as it is on screen.
 *
 * ### Delivery and enforcement (#568)
 *
 * Since the invitation can be **mailed**, this dialog also reports what became of that mail — and the link
 * stays on screen in every outcome, including `Sent`. That is the same fact stated twice: the server returns
 * the code regardless of the mail, so a dialog that hid it on a successful send would turn a bounced or
 * filtered message into a lost invitation. `Sent` is therefore a receipt with a fallback, and
 * `NoAddress`/`Failed` is a caution naming what the issuer must now do.
 *
 * An **enforced** binding is stated in the same caution panel, because it is the one setting that can make a
 * perfectly valid invitation unredeemable — an Apple "Hide My Email" recipient has no address the issuer could
 * ever have written down — and the only remedy is re-issuing, which only the person reading this can do.
 *
 * ### It fits on the screen it is opened on (#585)
 *
 * The dialog used to be one scrolling box: panel `max-h-[90vh] overflow-y-auto`, with the acknowledgement button
 * as the last thing inside it. On a 900px window a perfectly ordinary invitation — delivered by mail, bound to
 * an address — rendered three full cards above two labelled fields, and the only way out of the dialog was below
 * the fold. The exit to an unrecoverable secret must never be something somebody has to go looking for.
 *
 * Two changes, and both are structural rather than cosmetic:
 *
 *  * **The panel is a flex column with a scrolling BODY and a fixed footer.** The acknowledgement button sits
 *    outside the scroll container, so it is on screen at every viewport height and at every content length.
 *  * **The three status cards are one panel.** Delivery, the "shown only now" warning and the binding were three
 *    separately-bordered 1rem cards saying three things about the same act. They are now one caution panel plus,
 *    for the one outcome that is an action item, a short line above it — which is what gets the common case onto
 *    a 900px window rather than merely making it reachable.
 */
@Component({
  selector: 'company-invite-code-dialog',
  standalone: true,
  template: `
    <!-- No (click) on the overlay: a stray click outside must not destroy an unrecoverable secret. -->
    <div class="fixed inset-0 z-[1100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
      <!-- A flex COLUMN capped to the viewport (#585), not one scrolling box. The body below scrolls, the
           footer does not, so the only way out of the dialog is on screen at every window height. The cap uses
           dvh rather than vh because a mobile browser's toolbars make vh taller than what is actually visible,
           which is the same fold problem in a different disguise. -->
      <div class="flex max-h-[100dvh] w-full flex-col rounded-t-2xl bg-white shadow-xl
                  sm:max-h-[calc(100dvh-2rem)] sm:max-w-lg sm:rounded-2xl"
           role="dialog" aria-modal="true" aria-labelledby="invite-code-heading">
        <!-- min-h-0 is load-bearing: a flex item defaults to min-height:auto, which refuses to shrink below its
             content and would push the footer off screen exactly as before. -->
        <div id="invite-code-body" class="min-h-0 flex-1 space-y-3 overflow-y-auto p-4 sm:p-6">

          <div>
            <h2 id="invite-code-heading" class="text-xl font-bold tracking-tight text-gray-900">
              Einladung erstellt
            </h2>
            <p class="mt-1 text-sm text-gray-500">{{ target() }}</p>
          </div>

          @if (invitation().roles.length) {
            <div class="flex flex-wrap gap-1">
              @for (role of invitation().roles; track role) {
                <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs
                             font-semibold text-gray-600">{{ label(role) }}</span>
              }
            </div>
          }

          <!-- What became of the mail, when one was asked for (#568). A failed send is an action item and keeps
               its own surface; a successful one is a receipt and is a single line, because the caution panel
               below already tells the issuer what to do with the link either way. -->
          @if (deliveryMessage(); as message) {
            @if (deliveryFailed()) {
              <div class="rounded-xl border border-amber-100 bg-amber-50 p-3" role="alert">
                <p class="text-sm font-bold text-amber-800">Die Einladung wurde nicht per E-Mail versendet</p>
                <p class="mt-1 text-sm text-amber-800">{{ message }}</p>
              </div>
            } @else {
              <p class="text-sm font-semibold text-green-700" role="status" aria-live="polite">{{ message }}</p>
            }
          }

          <!-- ONE caution panel (#585). The remedy is named in it, because there is no other one, and the
               binding is stated here rather than in a card of its own: it is the same subject, and an enforced
               binding is exactly the setting that can turn this otherwise valid invitation into one nobody can
               redeem. -->
          <div class="rounded-xl border border-amber-100 bg-amber-50 p-3" role="alert">
            <p class="text-sm font-bold text-amber-800">Dieser Link wird nur jetzt angezeigt</p>
            <p class="mt-1 text-sm text-amber-800">
              CheqMe speichert den Einladungscode nicht, nur seine Prüfsumme. Er kann später nicht erneut
              angezeigt werden.
              @if (deliverySucceeded()) {
                Sichern Sie den Link jetzt, falls die E-Mail nicht ankommt.
              } @else {
                Kopieren Sie den Link jetzt und geben Sie ihn weiter.
              }
              Ist er verloren, widerrufen Sie die Einladung und stellen Sie eine neue aus.
            </p>
            @if (binding(); as sentence) {
              <p class="mt-1 text-sm font-semibold text-amber-800">{{ sentence }}</p>
              @if (invitation().enforceBoundEmail) {
                <p class="mt-1 text-sm text-amber-800">
                  Meldet sich die eingeladene Person mit Apple „E-Mail-Adresse verbergen“ oder mit einem Konto
                  ohne bestätigte Adresse an, wird die Einladung abgewiesen. Stellen Sie sie dann ohne die
                  Option „Nur mit dieser E-Mail-Adresse einlösbar“ neu aus.
                </p>
              }
            }
          </div>

          <div>
            <label for="invite-link" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
              Einladungslink
            </label>
            <div class="flex flex-col gap-2 sm:flex-row">
              <input id="invite-link" #linkField type="text" readonly [value]="link()"
                     class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 font-mono
                            text-xs text-gray-700 shadow-sm transition-all duration-200
                            focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500">
              <button type="button" (click)="copyLink()"
                      class="inline-flex shrink-0 items-center justify-center rounded-2xl bg-theme-primary-600
                             px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-theme-primary-100
                             transition-all duration-200 hover:bg-theme-primary-700 hover:shadow-xl
                             active:scale-95">
                Link kopieren
              </button>
            </div>
          </div>

          <div>
            <label for="invite-code" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
              Code (falls der Link nicht weitergegeben werden kann)
            </label>
            <div class="flex flex-col gap-2 sm:flex-row">
              <input id="invite-code" #codeField type="text" readonly [value]="invitation().code ?? ''"
                     class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 font-mono
                            text-sm font-semibold tracking-wide text-gray-900 shadow-sm transition-all
                            duration-200 focus:border-theme-primary-500 focus:ring-1
                            focus:ring-theme-primary-500">
              <button type="button" (click)="copyCode()"
                      class="inline-flex shrink-0 items-center justify-center rounded-2xl border
                             border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-600 shadow-sm
                             transition-all duration-200 hover:border-gray-300 hover:bg-gray-50
                             active:scale-95">
                Code kopieren
              </button>
            </div>
          </div>

          @if (feedback()) {
            <p class="text-sm font-semibold" role="status" aria-live="polite"
               [class.text-green-700]="copied()" [class.text-red-700]="!copied()">{{ feedback() }}</p>
          }
        </div>

        <!-- Outside the scroll container on purpose: this is the only exit from a dialog holding a secret that
             cannot be shown again, so it must never be below a fold. -->
        <div class="flex shrink-0 justify-end border-t border-gray-100 bg-white px-4 py-3 sm:px-6">
          <button type="button" (click)="acknowledge()"
                  class="inline-flex items-center justify-center rounded-2xl border border-gray-200 px-4
                         py-2.5 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200
                         hover:border-gray-300 hover:bg-gray-50 active:scale-95">
            Ich habe den Link gesichert — schließen
          </button>
        </div>
      </div>
    </div>
  `
})
export class InviteCodeDialogComponent {
  /** The invitation exactly as the issue call returned it — the only shape that carries a `code`. */
  readonly invitation = input.required<RoleGrantInvitation>();

  /** Raised only by the explicit close button. There is deliberately no other way out. */
  readonly closed = output<void>();

  private readonly linkField = viewChild<ElementRef<HTMLInputElement>>('linkField');
  private readonly codeField = viewChild<ElementRef<HTMLInputElement>>('codeField');

  protected readonly feedback = signal<string | null>(null);
  protected readonly copied = signal(false);

  protected readonly link = computed(() => inviteLink(this.invitation().code ?? ''));
  protected readonly target = computed(() => scopeDescription(this.invitation()));

  /**
   * What to say about the mail (#568) — `null` when none was asked for, in which case this dialog says nothing
   * about delivery at all. The wording lives in `grant.models.ts` because the employee roster reports the same
   * three outcomes in an alert banner, and two copies of that sentence is how one of them ends up lying.
   */
  protected readonly deliveryMessage = computed(() =>
    // The delivery address first (#584): since the binding toggle went away, an invitation can be mailed
    // somewhere without being bound to it, and naming `boundEmail` would then say "sent to nobody".
    inviteDeliveryMessage(this.invitation().emailDelivery,
      this.invitation().deliveryEmail ?? this.invitation().boundEmail));

  /** Whether the delivery outcome is one the issuer must act on — drives the caution surface over the receipt. */
  protected readonly deliveryFailed = computed(() => inviteDeliveryFailed(this.invitation().emailDelivery));

  /** Whether a mail was asked for AND went out, which is what decides how the caution panel phrases its advice. */
  protected readonly deliverySucceeded = computed(() => !!this.deliveryMessage() && !this.deliveryFailed());

  /** The address binding, in the strength it was issued at; `null` when the invitation names no address. */
  protected readonly binding = computed(() => bindingSentence(this.invitation()));

  protected readonly label = roleLabel;

  constructor() {
    // Put the caret in the link field as soon as the dialog appears, so a keyboard user reaches the thing they
    // came for without tabbing past the explanation — and so ⌘C works immediately if the clipboard API is
    // unavailable (Safari without a user gesture, an insecure origin).
    effect(() => {
      const field = this.linkField()?.nativeElement;
      if (field) field.focus();
    });
  }

  protected copyLink(): void {
    this.copy(this.link(), this.linkField()?.nativeElement, 'Link kopiert.');
  }

  protected copyCode(): void {
    this.copy(this.invitation().code ?? '', this.codeField()?.nativeElement, 'Code kopiert.');
  }

  protected acknowledge(): void {
    this.closed.emit();
  }

  /**
   * Copies through the async clipboard API, and when that is unavailable or refused **selects the text and
   * says so** rather than failing silently. A copy button that quietly does nothing is worse than no button:
   * the user believes they have the code, closes the dialog, and it is gone.
   */
  private copy(value: string, field: HTMLInputElement | undefined, success: string): void {
    const select = () => {
      field?.focus();
      field?.select();
    };

    const clipboard = navigator.clipboard;
    if (!clipboard?.writeText) {
      select();
      this.copied.set(false);
      this.feedback.set('Kopieren ist in diesem Browser nicht möglich — der Text ist markiert, bitte mit '
        + 'Strg/Cmd + C kopieren.');
      return;
    }

    clipboard.writeText(value).then(
      () => {
        this.copied.set(true);
        this.feedback.set(success);
      },
      () => {
        select();
        this.copied.set(false);
        this.feedback.set('Kopieren wurde vom Browser abgelehnt — der Text ist markiert, bitte mit '
          + 'Strg/Cmd + C kopieren.');
      });
  }
}
